import { useEffect, useRef, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Buildings, User, Globe, Camera, IdentificationCard, FloppyDisk, Images, LockKey, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);

  const load = () => api.get("/me/profile").then((r) => setP(r.data)).catch(() => setP({}));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", {
        photographer_name: p.photographer_name || "",
        business_name: p.business_name || "",
        website: p.website || "",
        bio: p.bio || "",
        name: p.name || "",
      });
      setP(data);
      toast.success("Profile saved");
    } catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.logo_url);
      toast.success("Logo updated");
    } catch { toast.error("Logo upload failed"); }
    finally { setUploading(false); }
  };

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
    finally { setChangingPw(false); }
  };

  if (!p) return <div className="grid h-64 place-items-center text-muted-foreground">Loading…</div>;

  const isBusiness = p.account_type === "business";

  return (
    <div className="mx-auto max-w-2xl">
      <p className="overline text-[#F27A54]">Account</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Your public profile</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        This is how you're credited across the Pulse network. It powers your photographer page and the attribution shown on sister sites.
      </p>

      <div className="mt-6 flex items-center gap-3">
        <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${isBusiness ? "bg-[#F27A54] text-white" : "bg-zinc-100 text-zinc-700"}`}>
          {isBusiness ? <Buildings size={14} weight="fill" /> : <User size={14} weight="fill" />}
          {isBusiness ? "Business account" : "Individual account"}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Images size={14} weight="fill" /> {p.submission_count || 0} submissions
        </span>
      </div>

      <div className="mt-6 space-y-6 rounded-xl border border-border bg-white p-6">
        {/* Logo (business emphasis, available to all) */}
        <div className="flex items-center gap-4">
          <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-zinc-50">
            {p.logo_url || p.avatar_url
              ? <img src={assetUrl(p.logo_url) || p.avatar_url} alt="logo" className="h-full w-full object-cover" data-testid="profile-logo-preview" />
              : (isBusiness ? <Buildings size={30} className="text-zinc-300" weight="thin" /> : <IdentificationCard size={30} className="text-zinc-300" weight="thin" />)}
          </div>
          <div>
            <p className="text-sm font-semibold text-zinc-800">{isBusiness ? "Business logo" : "Profile image"}</p>
            <p className="text-xs text-muted-foreground">Square PNG/JPG works best.</p>
            <button data-testid="profile-logo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-2 flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-[#F27A54] hover:bg-blue-50 disabled:opacity-60">
              <Camera size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload logo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} />
          </div>
        </div>

        {isBusiness && (
          <div>
            <Label htmlFor="pf-biz">Business name</Label>
            <Input id="pf-biz" data-testid="profile-business-name" value={p.business_name || ""}
              onChange={(e) => set("business_name", e.target.value)} placeholder="Acme Studios" className="mt-1.5" />
          </div>
        )}

        <div>
          <Label htmlFor="pf-name">{isBusiness ? "Display name (optional)" : "Your name"}</Label>
          <Input id="pf-name" data-testid="profile-name" value={p.name || ""}
            onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="mt-1.5" />
        </div>

        <div>
          <Label htmlFor="pf-photog">Photographer / byline name</Label>
          <Input id="pf-photog" data-testid="profile-photographer-name" value={p.photographer_name || ""}
            onChange={(e) => set("photographer_name", e.target.value)} placeholder="The name your photos are credited under" className="mt-1.5" />
          <p className="mt-1 text-xs text-muted-foreground">New uploads are attributed to this name and appear on your photographer page.</p>
        </div>

        <div>
          <Label htmlFor="pf-web">Website</Label>
          <div className="relative mt-1.5">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input id="pf-web" data-testid="profile-website" value={p.website || ""}
              onChange={(e) => set("website", e.target.value)} placeholder="https://your-site.com" className="pl-9" />
          </div>
        </div>

        <div>
          <Label htmlFor="pf-bio">{isBusiness ? "About the business" : "Bio"}</Label>
          <Textarea id="pf-bio" data-testid="profile-bio" value={p.bio || ""}
            onChange={(e) => set("bio", e.target.value)} rows={4} placeholder="Tell people about your work…" className="mt-1.5 resize-none" />
        </div>

        <div className="flex justify-end">
          <button data-testid="profile-save" onClick={save} disabled={saving}
            className="flex items-center gap-1.5 rounded-md bg-[#F27A54] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9633f] disabled:opacity-60">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>

      {p.has_password && (
        <div className="mt-6 space-y-5 rounded-xl border border-border bg-white p-6" data-testid="security-card">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F]"><ShieldCheck size={18} weight="fill" /></span>
            <div>
              <p className="text-sm font-bold text-zinc-800">Security</p>
              <p className="text-xs text-muted-foreground">Change the password you use for admin sign-in.</p>
            </div>
          </div>

          <div>
            <Label htmlFor="pw-current">Current password</Label>
            <div className="relative mt-1.5">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="pw-current" type="password" autoComplete="current-password" data-testid="pw-current"
                value={pw.current} onChange={(e) => setPw((x) => ({ ...x, current: e.target.value }))}
                placeholder="Enter current password" className="pl-9" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="pw-next">New password</Label>
              <Input id="pw-next" type="password" autoComplete="new-password" data-testid="pw-next"
                value={pw.next} onChange={(e) => setPw((x) => ({ ...x, next: e.target.value }))}
                placeholder="At least 8 characters" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input id="pw-confirm" type="password" autoComplete="new-password" data-testid="pw-confirm"
                value={pw.confirm} onChange={(e) => setPw((x) => ({ ...x, confirm: e.target.value }))}
                placeholder="Re-type new password" className="mt-1.5" />
            </div>
          </div>

          <div className="flex justify-end">
            <button data-testid="pw-submit" onClick={changePassword}
              disabled={changingPw || !pw.current || !pw.next || !pw.confirm}
              className="flex items-center gap-1.5 rounded-md bg-[#2D6A4F] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#245840] disabled:opacity-60">
              <LockKey size={16} weight="bold" /> {changingPw ? "Changing…" : "Change password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
